import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText,
  TrendingUp,
  CheckCircle2,
  Percent,
  Plus,
  MoreHorizontal,
  Pencil,
  Copy,
  Link2,
  Trash2,
  Send,
  Mail,
  XCircle,
  RefreshCw,
  Wallet,
  BadgeEuro,
  Eye,
  History,
  Activity,
  ExternalLink,
  Clock,
  CalendarPlus,
  Loader2,
  FileDown,
  Sheet,
  FileArchive,
} from "lucide-react";
import { api } from "../lib/api";
import { downloadQuotePdf, downloadCsv, downloadQuotesZip } from "../lib/quoteExport";
import { euro, fmtDate, daysLeft, timeAgo, leadTemp } from "../lib/format";
import StatusBadge from "../components/StatusBadge";
import ActivityLog from "../components/ActivityLog";
import FollowupModal from "../components/FollowupModal";
import BetaProgram from "../components/BetaProgram";
import ReviewPopup from "../components/ReviewPopup";
import { PlanBadge, UpgradeDialog, ProLock } from "../components/ProUpgrade";
import { Button } from "../components/ui/button";
import ZipPeriodDialog from "../components/dashboard/ZipPeriodDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "bozza", label: "Bozza" },
  { key: "inviato", label: "Inviato" },
  { key: "accettato", label: "Accettato" },
  { key: "rifiutato", label: "Rifiutato" },
];

const PAY_BADGE = {
  in_attesa: { label: "In attesa di acconto", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  bonifico_segnalato: { label: "Bonifico segnalato", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  pagato: { label: "Acconto pagato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-card border border-slate-200/80 rounded-xl p-5 shadow-sm hover:border-slate-300 transition-all duration-200">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon style={{ width: 16, height: 16 }} />
        </div>
      </div>
      <p data-testid={testid} className="font-mono text-2xl sm:text-3xl font-extrabold text-slate-900 mt-3 tracking-tight">
        {value}
      </p>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [activityQuote, setActivityQuote] = useState(null);
  const [followupQuote, setFollowupQuote] = useState(null);
  const [recent, setRecent] = useState([]);
  const [plan, setPlan] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    api.get("/billing/status").then((r) => setPlan(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, q, r] = await Promise.all([
      api.get("/quotes/stats"),
      api.get("/quotes", { params: { status: filter } }),
      api.get("/activity/recent"),
    ]);
    setStats(s.data);
    setQuotes(q.data);
    setRecent(r.data);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const versionNum = (qn) => parseInt((qn.match(/-v(\d+)$/) || [])[1] || "1", 10);
  const versionBase = (qn) => qn.replace(/-v\d+$/, "");

  const versionFamilies = useMemo(() => {
    const map = {};
    quotes.forEach((q) => {
      const base = versionBase(q.quote_number);
      (map[base] = map[base] || []).push(q);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => versionNum(a.quote_number) - versionNum(b.quote_number))
    );
    return map;
  }, [quotes]);

  const copyLink = (token) => {
    const url = `${window.location.origin}/p/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link cliente copiato negli appunti!");
  };

  const openAdminPreview = (token) => {
    window.open(`${window.location.origin}/p/${token}?preview=admin`, "_blank");
  };

  const exportPdf = (id) => downloadQuotePdf(id).catch(() => toast.error("Esportazione PDF non riuscita"));
  const exportItems = (q) => downloadCsv(`/quotes/${q.id}/items.csv`, `${q.quote_number || "preventivo"}-voci.csv`).catch(() => toast.error("Esportazione non riuscita"));
  const [zipProgress, setZipProgress] = useState(null);
  const [zipOpen, setZipOpen] = useState(false);
  const exportZip = () => {
    if (!quotes.length) { toast.error("Nessun preventivo in elenco da esportare"); return; }
    setZipOpen(true);
  };
  const runZip = async (list) => {
    setZipOpen(false);
    setZipProgress(`0/${list.length}`);
    try {
      await downloadQuotesZip(list.map((q) => q.id), (d, t) => setZipProgress(`${d}/${t}`));
    } catch (e) {
      toast.error("Creazione dello ZIP non riuscita");
    } finally { setZipProgress(null); }
  };
  const exportList = () => downloadCsv("/quotes/export.csv", `preventivi-${new Date().toISOString().slice(0, 10)}.csv`).catch(() => toast.error("Esportazione non riuscita"));
  const duplicate = async (id) => {
    await api.post(`/quotes/${id}/duplicate`);
    toast.success("Preventivo duplicato");
    load();
  };

  const newVersion = async (id) => {
    try {
      const { data } = await api.post(`/quotes/${id}/new-version`);
      toast.success(`Nuova versione creata: ${data.quote_number}`);
      navigate(`/quotes/${data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella creazione della nuova versione");
    }
  };

  const confirmDeposit = async (id) => {
    try {
      await api.post(`/quotes/${id}/confirm-deposit`);
      toast.success("Ricezione bonifico confermata — acconto pagato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella conferma");
    }
  };

  const extend = async (id, days) => {
    try {
      const { data } = await api.post(`/quotes/${id}/extend`, { days });
      toast.success(`Scadenza prorogata al ${fmtDate(data.due_date)}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella proroga");
    }
  };

  const extendCustom = async (id, date) => {
    try {
      const { data } = await api.post(`/quotes/${id}/extend`, { due_date: date });
      toast.success(`Scadenza prorogata al ${fmtDate(data.due_date)}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella proroga");
    }
  };

  const changeStatus = async (id, status) => {
    await api.patch(`/quotes/${id}/status`, { status });
    toast.success("Stato aggiornato");
    load();
  };

  const sendEmail = async (id) => {
    try {
      const { data } = await api.post(`/quotes/${id}/send`);
      toast.success(`Preventivo inviato a ${data.sent_to}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'invio dell'email");
    }
  };

  const openFollowup = (q) => {
    if (plan && plan.plan === "free") setUpgradeOpen(true);
    else setFollowupQuote(q);
  };

  const confirmDelete = async () => {
    await api.delete(`/quotes/${toDelete.id}`);
    setToDelete(null);
    toast.success("Preventivo eliminato");
    load();
  };

  return (
    <div>
      <BetaProgram />
      <ReviewPopup />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Dashboard</h1>
            {plan && <PlanBadge plan={plan.plan} founder={plan.founder_member} />}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Panoramica dei tuoi preventivi e della trattativa.
            {plan && plan.plan === "free" && (
              <> · <button data-testid="dashboard-upgrade-link" onClick={() => navigate("/billing")} className="text-primary font-medium hover:underline">Passa a Pro</button> ({plan.quotes_remaining} preventivi rimasti questo mese)</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="quotes-export-zip-btn" onClick={exportZip} disabled={!!zipProgress} className="h-10 font-semibold gap-1.5" title="Scarica in un file ZIP i PDF dei preventivi mostrati in elenco">
            {zipProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileArchive style={{ width: 16, height: 16 }} />}
            {zipProgress ? `PDF ${zipProgress}` : "SCARICA PDF"}
          </Button>
          <Button variant="outline" data-testid="quotes-export-csv-btn" onClick={exportList} className="h-10 font-semibold gap-1.5">
            <Sheet style={{ width: 16, height: 16 }} /> Esporta CSV
          </Button>
          <Button data-testid="new-quote-button" onClick={() => navigate("/quotes/new")} className="h-10 font-semibold">
            <Plus style={{ width: 16, height: 16 }} className="mr-1.5" />
            Nuovo Preventivo
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 mb-8">
        <StatCard testid="dashboard-stats-total-quotes" icon={FileText} label="Totale Preventivi" value={stats?.total_quotes ?? "—"} accent="bg-slate-100 text-slate-600" />
        <StatCard testid="dashboard-stats-total-value" icon={TrendingUp} label="Valore in Trattativa" value={stats ? euro(stats.pipeline_value) : "—"} accent="bg-indigo-50 text-indigo-600" />
        <StatCard testid="dashboard-stats-accepted-quotes" icon={CheckCircle2} label="Preventivi Accettati" value={stats?.accepted_quotes ?? "—"} accent="bg-emerald-50 text-emerald-600" />
        <StatCard testid="dashboard-stats-conversion-rate" icon={Percent} label="Tasso Conversione" value={stats ? `${stats.conversion_rate}%` : "—"} accent="bg-sky-50 text-sky-600" />
        <StatCard testid="dashboard-stats-deposits-collected" icon={Wallet} label="Totale Acconti Incassati" value={stats ? euro(stats.deposits_collected || 0) : "—"} accent="bg-emerald-50 text-emerald-600" />
      </div>

      <div className="bg-card border border-slate-200/80 rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center gap-1.5 p-3 border-b border-border overflow-x-auto qf-scrollbar">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`quotes-filter-status-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                filter === f.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="py-20 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nessun preventivo in questo stato.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {quotes.map((q) => {
              const dl = daysLeft(q.due_date);
              const family = versionFamilies[versionBase(q.quote_number)] || [q];
              const vnum = versionNum(q.quote_number);
              return (
                <div
                  key={q.id}
                  data-testid={`quote-row-${q.id}`}
                  className="flex items-center gap-4 px-4 sm:px-5 py-4 hover:bg-slate-50/60 transition-colors group"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-slate-400">{q.quote_number}</span>
                      {vnum > 1 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                          v{vnum}
                        </span>
                      )}
                      <StatusBadge status={q.status} />
                      {q.hot_lead && (
                        <span data-testid={`hot-lead-badge-${q.id}`} title={q.hot_lead_reason || ""} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">
                          Lead Caldo 🔥
                        </span>
                      )}
                      {PAY_BADGE[q.stato_pagamento_acconto] && (
                        <span data-testid={`payment-badge-${q.id}`} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${PAY_BADGE[q.stato_pagamento_acconto].cls}`}>
                          {PAY_BADGE[q.stato_pagamento_acconto].label}
                        </span>
                      )}
                      {(q.status === "inviato" || q.status === "visualizzato") && dl !== null && dl >= 0 && dl <= 2 && (
                        <span data-testid={`expiring-badge-${q.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-orange-50 text-orange-700 border-orange-200">
                          <Clock style={{ width: 11, height: 11 }} /> In scadenza {dl === 0 ? "oggi" : `tra ${dl} ${dl === 1 ? "giorno" : "giorni"}`}
                        </span>
                      )}
                      {q.rinnovo_richiesto && (
                        <span data-testid={`renewal-badge-${q.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-red-50 text-red-700 border-red-200 animate-pulse">
                          Rinnovo Richiesto!
                        </span>
                      )}
                    </div>
                    <p className="font-semibold text-slate-900 truncate mt-1">{q.title}</p>
                    <p className="text-sm text-slate-500 truncate">
                      {q.client?.name} · Scad. {fmtDate(q.due_date)}
                      {q.last_reminder_at && <span className="text-slate-400"> · Ultimo sollecito: {timeAgo(q.last_reminder_at)}</span>}
                    </p>
                    {family.length > 1 && (
                      <div data-testid={`quote-versions-${q.id}`} className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] text-slate-400">Versioni:</span>
                        {family.map((fq) => (
                          <button
                            key={fq.id}
                            onClick={() => navigate(`/quotes/${fq.id}/edit`)}
                            className={`text-[11px] font-semibold px-1.5 py-0.5 rounded transition-colors ${
                              fq.id === q.id
                                ? "bg-indigo-600 text-white"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            v{versionNum(fq.quote_number)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="hidden md:flex flex-col items-end gap-1 mr-1" data-testid={`engagement-${q.id}`}>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                      <Eye style={{ width: 12, height: 12 }} /> {q.totale_visite || 0} visite
                    </span>
                    {q.ultima_visita && <span className="text-[11px] text-slate-400">Ultima: {timeAgo(q.ultima_visita)}</span>}
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="font-mono font-bold text-slate-900">{euro(q.totals?.total, q.currency)}</p>
                    <p className="text-xs text-slate-400">{q.totals?.delivery_days} gg stimati</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid={`followup-quick-${q.id}`}
                    onClick={() => openFollowup(q)}
                    className="shrink-0 text-slate-400 hover:text-primary"
                    title="Invia Follow-up / Sollecito"
                  >
                    <Send style={{ width: 17, height: 17 }} />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`quote-actions-${q.id}`} className="shrink-0">
                        <MoreHorizontal style={{ width: 18, height: 18 }} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem data-testid={`action-edit-${q.id}`} onClick={() => navigate(`/quotes/${q.id}/edit`)}>
                        <Pencil style={{ width: 15, height: 15 }} className="mr-2" /> Modifica
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-copy-link-${q.id}`} onClick={() => copyLink(q.public_token)}>
                        <Link2 style={{ width: 15, height: 15 }} className="mr-2" /> Copia Link Cliente
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-admin-preview-${q.id}`} onClick={() => openAdminPreview(q.public_token)}>
                        <ExternalLink style={{ width: 15, height: 15 }} className="mr-2" /> Apri Anteprima Admin
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-activity-${q.id}`} onClick={() => setActivityQuote(q)}>
                        <History style={{ width: 15, height: 15 }} className="mr-2" /> Cronologia (Activity Log)
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-send-email-${q.id}`} onClick={() => sendEmail(q.id)}>
                        <Mail style={{ width: 15, height: 15 }} className="mr-2" /> Invia al Cliente via Email
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-pdf-${q.id}`} onClick={() => exportPdf(q.id)}>
                        <FileDown style={{ width: 15, height: 15 }} className="mr-2" /> Scarica PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-items-csv-${q.id}`} onClick={() => exportItems(q)}>
                        <Sheet style={{ width: 15, height: 15 }} className="mr-2" /> Esporta voci (CSV)
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-duplicate-${q.id}`} onClick={() => duplicate(q.id)}>
                        <Copy style={{ width: 15, height: 15 }} className="mr-2" /> Duplica
                      </DropdownMenuItem>
                      {(q.status === "rifiutato" || (q.status === "inviato" && dl !== null && dl < 0)) && (
                        <DropdownMenuItem data-testid={`action-new-version-${q.id}`} onClick={() => newVersion(q.id)}>
                          <RefreshCw style={{ width: 15, height: 15 }} className="mr-2" /> Riapri (nuova versione)
                        </DropdownMenuItem>
                      )}
                      {q.stato_pagamento_acconto === "bonifico_segnalato" && (
                        <DropdownMenuItem data-testid={`action-confirm-deposit-${q.id}`} onClick={() => confirmDeposit(q.id)} className="text-emerald-700 focus:text-emerald-700">
                          <BadgeEuro style={{ width: 15, height: 15 }} className="mr-2" /> Conferma ricezione bonifico
                        </DropdownMenuItem>
                      )}
                      {(q.status === "scaduto" || ((q.status === "inviato" || q.status === "visualizzato") && dl !== null && dl <= 2)) && (
                        <DropdownMenuItem data-testid={`action-extend-${q.id}`} onClick={() => extend(q.id, 7)}>
                          <CalendarPlus style={{ width: 15, height: 15 }} className="mr-2" /> Proroga Scadenza (+7 giorni)
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem data-testid={`action-followup-${q.id}`} onClick={() => openFollowup(q)}>
                        <Send style={{ width: 15, height: 15 }} className="mr-2" /> Invia Follow-up / Sollecito
                        {plan && plan.plan === "free" && <ProLock testid={`followup-prolock-${q.id}`} />}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {q.status !== "inviato" && (
                        <DropdownMenuItem onClick={() => changeStatus(q.id, "inviato")}>
                          <Send style={{ width: 15, height: 15 }} className="mr-2" /> Segna come Inviato
                        </DropdownMenuItem>
                      )}
                      {q.status !== "rifiutato" && (
                        <DropdownMenuItem onClick={() => changeStatus(q.id, "rifiutato")}>
                          <XCircle style={{ width: 15, height: 15 }} className="mr-2" /> Segna come Rifiutato
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        data-testid={`action-delete-${q.id}`}
                        onClick={() => setToDelete(q)}
                        className="text-red-600 focus:text-red-600"
                      >
                        <Trash2 style={{ width: 15, height: 15 }} className="mr-2" /> Elimina
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-8 bg-card border border-slate-200/80 rounded-xl shadow-sm p-5" data-testid="recent-activity-widget">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-primary" />
          <h2 className="text-base font-semibold text-slate-900">Attività Recenti in Tempo Reale</h2>
        </div>
        {recent.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Nessuna attività recente.</p>
        ) : (
          <div className="divide-y divide-border">
            {recent.map((l) => (
              <div key={l.id} className="flex items-start gap-3 py-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-700 leading-snug">
                    <span className="font-mono text-xs text-slate-400 mr-1.5">{l.quote_number}</span>
                    {l.message}
                  </p>
                </div>
                <span className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">{timeAgo(l.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il preventivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare "{toDelete?.title}". Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ActivityLog quote={activityQuote} onClose={() => setActivityQuote(null)} />
      <FollowupModal quote={followupQuote} onClose={() => setFollowupQuote(null)} onSent={load} />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} feature="Solleciti e Follow-up" />
      <ZipPeriodDialog open={zipOpen} onOpenChange={setZipOpen} quotes={quotes} onConfirm={runZip} />
    </div>
  );
}
